import { client } from '../../api'
import { env } from '../../config/env'
import { SearchParams, SearchResult } from '../../types'
import { ChatConversation, ChatStreamEvent, PageContext } from './types'

const base = (projectId: number | string) => `/admin/projects/${projectId}/ai`

export const chatApi = {
    enabled: async (projectId: number | string) => await client
        .get<{ enabled: boolean }>(`${base(projectId)}/enabled`)
        .then(r => r.data.enabled),

    conversations: {
        list: async (projectId: number | string, params: Partial<SearchParams> = {}) => await client
            .get<SearchResult<ChatConversation>>(`${base(projectId)}/conversations`, { params: { limit: 25, ...params } })
            .then(r => r.data),
        create: async (projectId: number | string) => await client
            .post<ChatConversation>(`${base(projectId)}/conversations`)
            .then(r => r.data),
        get: async (projectId: number | string, id: number) => await client
            .get<ChatConversation>(`${base(projectId)}/conversations/${id}`)
            .then(r => r.data),
        delete: async (projectId: number | string, id: number) => await client
            .delete(`${base(projectId)}/conversations/${id}`)
            .then(r => r.data),
    },

    stream: async function * (
        projectId: number | string,
        conversationId: number,
        content: string,
        signal: AbortSignal,
        pageContext?: PageContext,
    ): AsyncIterable<ChatStreamEvent> {
        const url = `${env.api.baseURL}${base(projectId)}/conversations/${conversationId}/stream`
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ content, page_context: pageContext }),
            signal,
        })

        if (!response.ok || !response.body) {
            const text = await response.text().catch(() => '')
            yield { type: 'error', message: text || `Request failed with ${response.status}` }
            return
        }

        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
        let buffer = ''
        try {
            while (true) {
                const { done, value } = await reader.read()
                if (done) break
                // Normalize CRLF → LF so boundary detection is line-ending agnostic.
                buffer += value.replace(/\r\n/g, '\n')

                let boundary = buffer.indexOf('\n\n')
                while (boundary !== -1) {
                    const block = buffer.slice(0, boundary)
                    buffer = buffer.slice(boundary + 2)

                    const dataLines: string[] = []
                    for (const line of block.split('\n')) {
                        if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
                    }
                    if (dataLines.length) {
                        try {
                            const event = JSON.parse(dataLines.join('\n')) as ChatStreamEvent
                            yield event
                        } catch {
                            // skip malformed
                        }
                    }
                    boundary = buffer.indexOf('\n\n')
                }
            }
        } catch (err: any) {
            if (err?.name !== 'AbortError') {
                yield { type: 'error', message: err?.message || 'Stream interrupted' }
            }
        } finally {
            try { await reader.cancel() } catch { /* ignore */ }
        }
    },
}
